import axios, {
    type AxiosError,
    type AxiosResponse,
    type InternalAxiosRequestConfig,
} from 'axios';
import type { AuthSession } from '@/api/models';
import { PATH } from '@/router/PATH';
import {
    clearAuthSession,
    clearDeviceSession,
    getAccessToken,
    getDeviceToken,
    getRefreshToken,
    SESSION_REVOKED,
    type SignOutReason,
    setSession,
} from '@/utils';
import {
    AUTH_PATHS,
    isAnonymousAuthEndpoint,
    isDeviceTokenEndpoint,
} from './authPaths';

/** Axios config carrying our single-retry guard so a refresh can't loop. */
type RetryableConfig = InternalAxiosRequestConfig & {
    __isAuthRetry?: boolean;
};

const setAuthHeader = (
    config: InternalAxiosRequestConfig,
    token: string,
): void => {
    const headers = config.headers ?? {};
    if (typeof (headers as { set?: unknown }).set === 'function') {
        (headers as { set: (k: string, v: string) => void }).set(
            'Authorization',
            `Bearer ${token}`,
        );
    } else {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    config.headers = headers as InternalAxiosRequestConfig['headers'];
};

/**
 * Request interceptor: attaches the right credential per endpoint.
 * - device-token endpoints (roster, PIN) → the device token
 * - anonymous endpoints (device exchange, refresh, logout) → no header
 * - everything else (catalog, sales) → the seller access token
 */
export const authRequestInterceptor = (
    config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
    if (isAnonymousAuthEndpoint(config.url)) {
        return config;
    }
    const token = isDeviceTokenEndpoint(config.url)
        ? getDeviceToken()
        : getAccessToken();
    if (token) {
        setAuthHeader(config, token);
    }
    return config;
};

/**
 * How a refresh attempt ended. Only `rejected` ends the session: the server
 * refused the refresh token itself, and `reason` says why when the seller must
 * be told. `unavailable` is temporary trouble (429, 5xx, network, an unreadable
 * reply) that must never sign the seller out.
 */
export type RefreshOutcome =
    | { status: 'refreshed'; accessToken: string }
    | { status: 'rejected'; reason?: SignOutReason }
    | { status: 'unavailable'; error: unknown };

const REJECTED: RefreshOutcome = { status: 'rejected' };

/**
 * Sort a failed refresh call. BosApi refuses an invalid, expired or replayed
 * refresh token with a 400 (a 401 counts too); only those end the session. A
 * `SESSION_REVOKED` refusal also ends it, and records that the seller signed in
 * on another sale point. Anything else (429, 5xx, a proxy's 404 or 408, no
 * response at all) is temporary, so a misbehaving network never signs the
 * seller out.
 */
export const classifyRefreshFailure = (error: unknown): RefreshOutcome => {
    const response = (
        error as {
            response?: { status?: unknown; data?: { code?: unknown } };
        } | null
    )?.response;
    if (response?.status !== 400 && response?.status !== 401) {
        return { status: 'unavailable', error };
    }
    return response.data?.code === SESSION_REVOKED
        ? { status: 'rejected', reason: 'sessionRevoked' }
        : REJECTED;
};

// Single-flight guard: concurrent 401s share one refresh round-trip instead of
// each firing their own (which would rotate the refresh token N times and
// invalidate all but the last).
let inFlightRefresh: Promise<RefreshOutcome> | null = null;

const performRefresh = async (baseURL: string): Promise<RefreshOutcome> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return REJECTED;
    }
    if (!baseURL) {
        // A configuration problem, not a verdict on the token.
        return { status: 'unavailable', error: new Error('No API base URL') };
    }
    try {
        // A bare, interceptor-less instance: the refresh call must never be
        // caught by the 401 handler (it would loop) and carries no bearer.
        const bare = axios.create({ baseURL, withCredentials: true });
        const response = await bare.post(AUTH_PATHS.REFRESH, { refreshToken });
        const data = response.data as Partial<AuthSession>;
        if (
            data &&
            typeof data.accessToken === 'string' &&
            typeof data.refreshToken === 'string' &&
            data.user
        ) {
            setSession(data as AuthSession);
            return { status: 'refreshed', accessToken: data.accessToken };
        }
        // An unreadable 2xx (e.g. a proxy or captive-portal page) is not the
        // server rejecting the token: keep the session and let a retry decide.
        return { status: 'unavailable', error: response };
    } catch (error) {
        return classifyRefreshFailure(error);
    }
};

/**
 * Rotate the seller access/refresh pair, de-duplicated across concurrent
 * callers (the interceptor and the route guard share one round-trip).
 */
export const refreshUserToken = (baseURL: string): Promise<RefreshOutcome> => {
    if (!inFlightRefresh) {
        inFlightRefresh = performRefresh(baseURL).finally(() => {
            inFlightRefresh = null;
        });
    }
    return inFlightRefresh;
};

const redirectToPath = (path: string): void => {
    if (
        typeof window !== 'undefined' &&
        window.location &&
        window.location.pathname !== path
    ) {
        window.location.assign(path);
    }
};

/** Flag `makeApiRequest` copies into `ErrorResponse.isRetryable`. */
const markRetryable = (error: AxiosError): AxiosError => {
    (error as AxiosError & { isRetryable?: boolean }).isRetryable = true;
    return error;
};

type ResponseRejectionDeps = {
    instance: { request: (config: RetryableConfig) => Promise<AxiosResponse> };
    refresh?: (baseURL: string) => Promise<RefreshOutcome>;
    redirect?: (path: string) => void;
};

/**
 * 401 / 403 response interceptor. The routing is asymmetric by design:
 * - a **device-endpoint** 401 OR 403 (roster / PIN / PIN-verify) means the
 *   device token is dead — expired/bad (401) or the branch token was
 *   rotated/revoked so the server's DeviceTokenStillValid policy refuses it
 *   (403) → drop the device session and send the terminal back to
 *   **enrollment**.
 * - a **business-endpoint** 401 (catalog, sales) means the seller
 *   access token lapsed → single-flight refresh and retry once. If the server
 *   rejects the refresh token, drop only the seller session (keep the terminal
 *   enrolled) and send back to **seller-select**. If the refresh hits a 429, a
 *   5xx or the network, keep the session and fail the original request,
 *   marked `isRetryable`. A business-endpoint **403** is a genuine authorization
 *   denial (wrong role / scope) and is surfaced verbatim — never refreshed.
 * - **anonymous** endpoints (device exchange, refresh, logout) are surfaced
 *   verbatim — refreshing on them would loop.
 */
export const createAuthResponseRejectionHandler =
    ({
        instance,
        refresh = refreshUserToken,
        redirect = redirectToPath,
    }: ResponseRejectionDeps) =>
    async (error: AxiosError): Promise<AxiosResponse> => {
        const status = error.response?.status;
        const config = error.config as RetryableConfig | undefined;

        // Recoverable auth failures only: a 401 anywhere, or a 403 on a
        // device-token endpoint — the branch API token was rotated/revoked,
        // so the server's DeviceTokenStillValid policy refuses the now-orphaned
        // device token (it authenticates fine, hence 403 not 401).
        if (!config || (status !== 401 && status !== 403)) {
            return Promise.reject(error);
        }

        if (isAnonymousAuthEndpoint(config.url)) {
            return Promise.reject(error);
        }

        if (isDeviceTokenEndpoint(config.url)) {
            // A device-endpoint 401 (expired / bad token) or 403 (branch token
            // rotated/revoked) both mean this device token is dead. Drop it and
            // re-enroll to mint a fresh one from the current branch token.
            clearDeviceSession();
            redirect(PATH.ENROLL);
            return Promise.reject(error);
        }

        // Past here is the seller-token refresh path, which only applies to a
        // lapsed access token (401). A 403 on a business endpoint is a real
        // authorization denial (wrong role / scope) — surface it untouched.
        if (status !== 401) {
            return Promise.reject(error);
        }

        // A retried business call that 401s again: refresh didn't help — bounce.
        if (config.__isAuthRetry) {
            clearAuthSession();
            redirect(PATH.LOGIN);
            return Promise.reject(error);
        }

        const outcome = await refresh(config.baseURL ?? '');
        if (outcome.status === 'unavailable') {
            // Temporary trouble: keep the session so the next attempt can
            // refresh, and fail the original request as worth retrying.
            return Promise.reject(markRetryable(error));
        }
        if (outcome.status === 'rejected') {
            // The reason survives the full-page redirect for the seller picker.
            clearAuthSession(outcome.reason);
            redirect(PATH.LOGIN);
            return Promise.reject(error);
        }

        config.__isAuthRetry = true;
        setAuthHeader(config, outcome.accessToken);
        return instance.request(config);
    };
